import { Component, Input, OnInit, inject, DestroyRef } from '@angular/core';
import { ServiceCardService } from '../../../services/util/service-card.service';
import { SolutionsService } from '../../../services/api/solutions.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ServiceCardInfo } from '../../../models/service/service-card-info';
import { ServiceStatus, serviceStatuses } from '../../../models/service/service-status';
import { AuthService } from '../../../services/auth/auth.service';
import { RouterSegments } from 'src/app/models/router-segments';
import { Solution } from 'src/app/models/service/solution';
import { SolutionVersion } from 'src/app/models/service/solution-version';
import { map, mergeMap } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { KeyValueMap } from 'src/app/models/key-value-map';

type FilterFunc = (cardInfo: ServiceCardInfo, filter: string) => boolean;
@Component({
    selector: 'app-my-services',
    templateUrl: './my-services.component.html',
    styleUrls: ['./my-services.component.scss'],
    standalone: false
})
export class MyServicesComponent implements OnInit {

  @Input() showTitle: boolean;
  tenant: string;

  servicesVersionsMap = new Map<string, SolutionVersion[]>();

  cardInfos: ServiceCardInfo[] = [];
  filteredCardInfos: ServiceCardInfo[] = [];
  filters: Set<string> = new Set();
  filterToCount: Map<string, number> = new Map();

  totalCount = 0;
  serviceStatuses = serviceStatuses;

  RouterSegments = RouterSegments;
  private destroyRef = inject(DestroyRef);

  SANDBOXES = 'SANDBOXES';
  FILTERS: KeyValueMap<string, FilterFunc> = {
    [ServiceStatus.DOWN]: this.isInStatus,
    [ServiceStatus.IN_PROGRESS]: this.isInStatus,
    [ServiceStatus.LIVE]: this.isInStatus,
    [this.SANDBOXES]: this.isSandbox
  };
  serviceFilters = Object.keys(this.FILTERS);

  constructor(private serviceCardService: ServiceCardService,
              private solutionsService: SolutionsService,
              private authService: AuthService) {
  }

  ngOnInit(): void {
    this.tenant = this.authService.principal.provider;
    this.loadSolutions();
  }

  selectFilter(filter: string): void {
    if (this.filters.has(filter)) {
      this.filters.delete(filter);
    } else {
      this.filters.add(filter);
    }
    this.applyFilter();
  }

  clearFilter(): void {
    this.filters.clear();
    this.applyFilter();
  }

  onBlueprintDeleted(cardInfo: ServiceCardInfo): void {
    this.cardInfos.splice(this.cardInfos.indexOf(cardInfo), 1);
    this.calculateCounts();
    this.applyFilter();
  }

  /**
   * Loads the solutions and for each solution loads its versions and prepares the Map
   * containing the needed information for the cards
   */
  private loadSolutions(): void {
    this.solutionsService.getMySolutions()
        .pipe(
          mergeMap((solutions: any[]) => forkJoin(
              solutions.map(solution => this.solutionsService.getSolutionVersions(solution.id)
                .pipe(
                  map((versions: any) => {
                    this.servicesVersionsMap.set(solution.id, versions);
                    return solution;
                  })
              ))
          )),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(solutions => {
          this.loadServiceCardInformation(solutions);
        });
  }

  /**
   * Loads the information needed for displaying service cards
   */
  private loadServiceCardInformation(services: Solution[]): void {
    this.serviceCardService.getServiceCardInfos(services, this.servicesVersionsMap)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(cardInfos => {
          this.cardInfos = cardInfos.filter(cardInfo => !cardInfo.blueprint.demo || cardInfo.instances.length);
          this.filteredCardInfos = this.cardInfos;
          this.calculateCounts();
        });
  }

  private calculateCounts(): void {
    this.mapStatusToCount(this.cardInfos);
    this.totalCount = this.cardInfos.length;
  }

  private mapStatusToCount(cardInfos: ServiceCardInfo[]): void {
    this.filterToCount.clear();
    this.serviceFilters.forEach(filter => {
          this.filterToCount.set(filter, cardInfos.filter(cardInfo => this.FILTERS[filter](cardInfo, filter)).length);
    });
  }

  private applyFilter(): void {
    if (this.filters.size === 0) {
      this.filteredCardInfos = this.cardInfos;
    }
    else {
      const filtersArray = Array.from(this.filters);
      this.filteredCardInfos = this.cardInfos.filter(cardInfo =>
        filtersArray.some(filter => this.FILTERS[filter](cardInfo, filter)));
    }
  }

  private isSandbox(cardInfo: ServiceCardInfo, filter: string): boolean {
    return cardInfo.blueprint.demo;
  }

  private isInStatus(cardInfo: ServiceCardInfo, filter: string): boolean {
    return cardInfo.status === filter;
  }
}
